/**
 * KategorieEditModal — edit one web category (platform admins, Platforma → Půjčovna). Create is the
 * full-page `KategorieForm`; both share `KategorieFormBody`. PUTs a slice-only payload.
 */

import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { Modal, DialogCrown } from '@/components/Modal';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useUpdateKategorieMutation, type PujcovnaKategorie } from './api';
import { slugSeMeni } from './otviraci-doba-helpers';
import { KATEGORIE_FORM_ID, KategorieFormBody, kategorieSeed, kategorieToBody } from './kategorie-section-forms';

interface KategorieEditModalProps {
  kategorie: PujcovnaKategorie;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function KategorieEditModal({ kategorie, open, onOpenChange }: KategorieEditModalProps) {
  const update = useUpdateKategorieMutation(kategorie.id);
  const { confirm, dialog } = useConfirm();

  return (
    <>
      <Modal open={open} onOpenChange={onOpenChange} maxWidth={560}>
        <DialogCrown
          eyebrow="Kategorie"
          title={
            <>
              Upravit <em>{kategorie.nazev}</em>
            </>
          }
          sub="Název, klíč ikony a odkaz na webu kategorie vozidel půjčovny."
        />
        <Modal.Body className="px-8 py-5">
          <KategorieFormBody
            initial={kategorieSeed(kategorie)}
            busy={update.isPending}
            onSubmit={async (form) => {
              if (!form.nazev.trim()) {
                toast.error('Zadejte název kategorie.');
                return;
              }
              // The key is part of the public URL: changing or clearing it breaks every link already out there.
              if (slugSeMeni(kategorie.slug, form.slug)) {
                const ok = await confirm({
                  title: (
                    <>
                      Změnit <em>odkaz na webu</em>?
                    </>
                  ),
                  description: `Dosavadní odkazy na kategorii (…?kategorie=${kategorie.slug}) přestanou fungovat${
                    form.slug.trim() ? '.' : ' a kategorie zmizí z veřejné nabídky.'
                  }`,
                  tone: 'danger',
                  confirmLabel: 'Změnit odkaz',
                });
                if (!ok) return;
              }
              update.mutate(kategorieToBody(form), {
                onSuccess: () => {
                  toast.ok('Kategorie uložena.');
                  onOpenChange(false);
                },
                onError: (err) =>
                  toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit kategorii.'}`),
              });
            }}
          />
        </Modal.Body>
        <Modal.Foot
          actions={
            <>
              <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={update.isPending}>
                Zrušit
              </Button>
              <Button variant="ink" type="submit" form={KATEGORIE_FORM_ID} disabled={update.isPending}>
                Uložit
              </Button>
            </>
          }
        />
      </Modal>
    {dialog}
    </>
  );
}
